import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import { absUrl } from "@/lib/config";
import {
  Wallet, Plus, Trash, FloppyDisk, PaperPlaneTilt, CheckCircle, XCircle,
  ArrowClockwise, UploadSimple, DownloadSimple, ArrowUUpLeft, PencilSimple,
  ChartPie, Coins, Receipt, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const STATUS_STYLES = {
  draft:     { cls: "bg-muted text-foreground",     label: "DRAFT" },
  submitted: { cls: "bg-[#F59E0B] text-white",      label: "PENDING" },
  approved:  { cls: "bg-[#22C55E] text-white",      label: "APPROVED" },
  rejected:  { cls: "bg-[#FF3B30] text-white",      label: "REJECTED" },
  paid:      { cls: "bg-[#002FA7] text-white",      label: "PAID" },
};

const fmtMoney = (amount, currency) => {
  const n = Number(amount || 0);
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 });
  } catch {
    return `${currency || ""} ${n.toFixed(2)}`;
  }
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// =========== EXPENSE FORM MODAL ===========
function ExpenseFormModal({ open, onClose, initial, projects, cfg, onSaved }) {
  const isEdit = !!initial?.id;
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(cfg.default_currency);
  const [dt, setDt] = useState(todayISO());
  const [category, setCategory] = useState(cfg.categories?.[0]?.key || "other");
  const [projectId, setProjectId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("reimbursable");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [receipt, setReceipt] = useState(null); // { id, filename, size, content_type }
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setAmount(String(initial.amount ?? ""));
      setCurrency(initial.currency || cfg.default_currency);
      setDt(initial.date || todayISO());
      setCategory(initial.category || cfg.categories?.[0]?.key || "other");
      setProjectId(initial.project_id || "");
      setPaymentMethod(initial.payment_method || "reimbursable");
      setVendor(initial.vendor || "");
      setDescription(initial.description || "");
      setReceipt(initial.receipt || null);
    } else {
      setAmount(""); setCurrency(cfg.default_currency); setDt(todayISO());
      setCategory(cfg.categories?.[0]?.key || "other"); setProjectId("");
      setPaymentMethod("reimbursable"); setVendor(""); setDescription("");
      setReceipt(null);
    }
    setMsg("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, cfg.default_currency]);

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceipt({ id: r.data.id, filename: file.name, length: file.size, content_type: file.type });
    } catch (e) {
      setMsg(formatAxiosError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setMsg("Amount must be greater than 0."); return; }
    if (!dt) { setMsg("Date is required."); return; }
    if (!category) { setMsg("Category is required."); return; }
    setSaving(true);
    setMsg("");
    try {
      const payload = {
        amount: amt,
        currency: (currency || cfg.default_currency).toUpperCase(),
        date: dt,
        category,
        project_id: projectId || null,
        payment_method: paymentMethod,
        vendor: vendor.trim(),
        description: description.trim(),
        receipt_file_id: receipt?.id || null,
      };
      if (isEdit) {
        await api.patch(`/expenses/${initial.id}`, payload);
      } else {
        await api.post("/expenses", payload);
      }
      await onSaved();
      onClose();
    } catch (e) {
      setMsg(formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="expense-form-modal">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow flex items-center gap-2 mb-1">
              <Receipt size={12} weight="bold" /> {isEdit ? "Edit expense" : "New expense claim"}
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight">
              {isEdit ? "Update expense" : "Submit an expense"}
            </h2>
          </div>
          <button onClick={onClose} className="border border-border p-2 hover:bg-muted"><X size={14} weight="bold" /></button>
        </div>

        <div className="p-6 space-y-4">
          {msg && <div className="px-3 py-2 border border-[#FF3B30] bg-red-50 text-sm text-[#FF3B30]">{msg}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">Amount *</label>
              <input
                type="number" step="0.01" min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                placeholder="0.00"
                data-testid="expense-amount"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">Currency</label>
              <input
                type="text"
                value={currency}
                onChange={e => setCurrency(e.target.value.toUpperCase())}
                maxLength={6}
                className="w-full border border-border px-3 py-2 text-sm uppercase"
                data-testid="expense-currency"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">Date *</label>
              <input
                type="date"
                value={dt}
                onChange={e => setDt(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="expense-date"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">Category *</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="expense-category"
              >
                {(cfg.categories || []).map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">Project (optional)</label>
              <select
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="expense-project"
              >
                <option value="">— none —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-1">Payment method</label>
              <select
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="expense-payment-method"
              >
                <option value="reimbursable">Reimbursable</option>
                <option value="card">Company card</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-1">Vendor / merchant</label>
            <input
              type="text"
              value={vendor}
              onChange={e => setVendor(e.target.value)}
              className="w-full border border-border px-3 py-2 text-sm"
              placeholder="e.g. Uber, Delta Airlines, Amazon"
              data-testid="expense-vendor"
            />
          </div>

          <div>
            <label className="eyebrow block mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-border px-3 py-2 text-sm"
              placeholder="What was this for? Add context for the approver."
              data-testid="expense-description"
            />
          </div>

          <div>
            <label className="eyebrow block mb-1">Receipt</label>
            {receipt ? (
              <div className="border border-border p-3 flex items-center justify-between gap-3">
                <div className="text-sm truncate">
                  <span className="font-semibold">{receipt.filename}</span>
                  <span className="text-muted-foreground ml-2 text-[11px]">({(receipt.length / 1024).toFixed(1)} KB)</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={absUrl(`/api/files/${receipt.id}`)}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1"
                  >
                    <DownloadSimple size={12} weight="bold" /> View
                  </a>
                  <button onClick={() => setReceipt(null)} className="border border-border p-1 hover:bg-muted" title="Remove">
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="border border-dashed border-border p-4 flex items-center justify-center gap-2 text-sm cursor-pointer hover:bg-muted">
                <UploadSimple size={14} weight="bold" />
                {uploading ? "Uploading…" : "Attach receipt (PDF or image)"}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={e => uploadReceipt(e.target.files?.[0])}
                  data-testid="expense-receipt-input"
                />
              </label>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
          <button
            onClick={save}
            disabled={saving || uploading}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
            data-testid="expense-save"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =========== EXPENSE ROW ===========
function ExpenseRow({ x, isOwner, canApprove, canPay, onEdit, onSubmit, onWithdraw, onDelete, onApprove, onReject, onMarkPaid }) {
  const st = STATUS_STYLES[x.status] || STATUS_STYLES.draft;
  return (
    <tr className="border-b border-border hover:bg-muted/30" data-testid={`expense-row-${x.id}`}>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{x.date}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-mono-mini px-1.5 py-0.5 text-white"
            style={{ backgroundColor: x.category_meta?.color || "#6B7280" }}
          >
            {x.category_meta?.label || x.category}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 text-sm">
        {x.user && (
          <div className="flex items-center gap-2">
            {x.user.avatar
              ? <img src={absUrl(x.user.avatar)} alt="" className="w-6 h-6 object-cover rounded-full" />
              : <div className="w-6 h-6 rounded-full bg-muted text-[10px] flex items-center justify-center">{(x.user.name || "?").slice(0, 1)}</div>}
            <span className="text-sm">{x.user.name}</span>
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-sm">{x.vendor || <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2 text-xs">{x.project?.name || <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2 text-sm font-semibold text-right whitespace-nowrap">
        {fmtMoney(x.amount, x.currency)}
      </td>
      <td className="px-3 py-2">
        <span className={`text-[10px] px-1.5 py-0.5 font-mono-mini ${st.cls}`}>{st.label}</span>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center gap-1 justify-end">
          {x.receipt && (
            <a
              href={absUrl(`/api/files/${x.receipt.id}`)}
              target="_blank"
              rel="noreferrer"
              className="border border-border p-1.5 hover:bg-muted"
              title="View receipt"
            >
              <DownloadSimple size={12} weight="bold" />
            </a>
          )}
          {isOwner && x.status === "draft" && (
            <>
              <button onClick={() => onEdit(x)} className="border border-border p-1.5 hover:bg-muted" title="Edit">
                <PencilSimple size={12} weight="bold" />
              </button>
              <button onClick={() => onSubmit(x)} className="btn-primary px-2 py-1 text-xs flex items-center gap-1" data-testid={`submit-${x.id}`}>
                <PaperPlaneTilt size={12} weight="bold" /> Submit
              </button>
              <button onClick={() => onDelete(x)} className="border border-border p-1.5 hover:bg-muted" title="Delete">
                <Trash size={12} weight="bold" />
              </button>
            </>
          )}
          {isOwner && x.status === "rejected" && (
            <>
              <button onClick={() => onEdit(x)} className="border border-border p-1.5 hover:bg-muted" title="Edit and resubmit">
                <PencilSimple size={12} weight="bold" />
              </button>
              <button onClick={() => onSubmit(x)} className="btn-primary px-2 py-1 text-xs flex items-center gap-1">
                <PaperPlaneTilt size={12} weight="bold" /> Resubmit
              </button>
            </>
          )}
          {isOwner && x.status === "submitted" && (
            <button onClick={() => onWithdraw(x)} className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1" title="Withdraw before approver acts">
              <ArrowUUpLeft size={12} weight="bold" /> Withdraw
            </button>
          )}
          {canApprove && x.status === "submitted" && (
            <>
              <button onClick={() => onReject(x)} className="border border-[#FF3B30] text-[#FF3B30] px-2 py-1 text-xs hover:bg-red-50 flex items-center gap-1" data-testid={`reject-${x.id}`}>
                <XCircle size={12} weight="bold" /> Reject
              </button>
              <button onClick={() => onApprove(x)} className="bg-[#22C55E] text-white px-2 py-1 text-xs hover:opacity-90 flex items-center gap-1" data-testid={`approve-${x.id}`}>
                <CheckCircle size={12} weight="bold" /> Approve
              </button>
            </>
          )}
          {canPay && x.status === "approved" && (
            <button onClick={() => onMarkPaid(x)} className="bg-[#002FA7] text-white px-2 py-1 text-xs hover:opacity-90 flex items-center gap-1" data-testid={`mark-paid-${x.id}`}>
              <Coins size={12} weight="bold" /> Mark paid
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// =========== MY EXPENSES TAB ===========
function MyExpensesTab({ cfg, projects }) {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/expenses${q}`);
      setRows(r.data.filter(x => x.user_id === user?.id));
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setLoading(false); }
  }, [statusFilter, user?.id]);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const submit = async (x) => {
    try { await api.post(`/expenses/${x.id}/submit`); await load(); flash("ok", "Submitted for approval."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  const withdraw = async (x) => {
    const ok = await confirm({
      title: "Withdraw expense?",
      description: "This will move the expense back to a draft so you can edit or delete it.",
      confirmText: "Withdraw",
    });
    if (!ok) return;
    try { await api.post(`/expenses/${x.id}/withdraw`); await load(); flash("ok", "Withdrawn."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  const remove = async (x) => {
    const ok = await confirm({
      title: "Delete this expense draft?",
      description: "The draft and any attached receipts will be permanently deleted.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/expenses/${x.id}`); await load(); flash("ok", "Deleted."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  const totals = useMemo(() => {
    const g = { count: 0, total: 0 };
    rows.forEach(r => { g.count++; g.total += Number(r.amount || 0); });
    return g;
  }, [rows]);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-sm text-muted-foreground max-w-xl">
            Track your expense claims. Save as draft, then submit for approval. You can edit or withdraw
            while it&apos;s still in DRAFT or SUBMITTED (before an approver acts).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-border px-3 py-2 text-sm"
            data-testid="my-status-filter"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="new-expense-button"
          >
            <Plus size={14} weight="bold" /> New expense
          </button>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-border p-4">
          <div className="eyebrow">Total claims</div>
          <div className="font-display text-3xl font-black">{totals.count}</div>
        </div>
        <div className="border border-border p-4">
          <div className="eyebrow">Total amount</div>
          <div className="font-display text-3xl font-black">{fmtMoney(totals.total, cfg.default_currency)}</div>
        </div>
        <div className="border border-border p-4">
          <div className="eyebrow">Pending</div>
          <div className="font-display text-3xl font-black">
            {rows.filter(r => r.status === "submitted").length}
          </div>
        </div>
        <div className="border border-border p-4">
          <div className="eyebrow">Approved</div>
          <div className="font-display text-3xl font-black text-[#22C55E]">
            {rows.filter(r => r.status === "approved" || r.status === "paid").length}
          </div>
        </div>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="px-3 py-2 eyebrow">Date</th>
              <th className="px-3 py-2 eyebrow">Category</th>
              <th className="px-3 py-2 eyebrow">Submitter</th>
              <th className="px-3 py-2 eyebrow">Vendor</th>
              <th className="px-3 py-2 eyebrow">Project</th>
              <th className="px-3 py-2 eyebrow text-right">Amount</th>
              <th className="px-3 py-2 eyebrow">Status</th>
              <th className="px-3 py-2 eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No expenses yet. Click &quot;New expense&quot; to start.</td></tr>
            )}
            {rows.map(x => (
              <ExpenseRow
                key={x.id}
                x={x}
                isOwner={true}
                canApprove={false}
                canPay={false}
                onEdit={(x) => { setEditing(x); setShowForm(true); }}
                onSubmit={submit}
                onWithdraw={withdraw}
                onDelete={remove}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ExpenseFormModal
        open={showForm}
        onClose={() => setShowForm(false)}
        initial={editing}
        projects={projects}
        cfg={cfg}
        onSaved={load}
      />
    </div>
  );
}

// =========== APPROVALS TAB ===========
function ApprovalsTab({ cfg }) {
  const { can } = useCapabilities();
  const canPay = can("mark_expenses_paid");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [rejectDlg, setRejectDlg] = useState(null); // expense object
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/expenses/pending-for-me`);
      setRows(r.data || []);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const approve = async (x) => {
    try { await api.post(`/expenses/${x.id}/approve`); await load(); flash("ok", "Approved."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  const openReject = (x) => { setRejectDlg(x); setRejectReason(""); };
  const submitReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      await api.post(`/expenses/${rejectDlg.id}/reject`, { reason: rejectReason.trim() });
      setRejectDlg(null); setRejectReason("");
      await load();
      flash("ok", "Rejected.");
    } catch (e) { flash("error", formatAxiosError(e)); }
  };

  const totalPending = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-sm text-muted-foreground max-w-xl">
            All submitted expense claims awaiting your approval. Approve to move to reimbursement, or
            reject with a reason so the submitter can revise.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Pending total:</span>
          <span className="font-display text-xl font-black">{fmtMoney(totalPending, cfg.default_currency)}</span>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="px-3 py-2 eyebrow">Date</th>
              <th className="px-3 py-2 eyebrow">Category</th>
              <th className="px-3 py-2 eyebrow">Submitter</th>
              <th className="px-3 py-2 eyebrow">Vendor</th>
              <th className="px-3 py-2 eyebrow">Project</th>
              <th className="px-3 py-2 eyebrow text-right">Amount</th>
              <th className="px-3 py-2 eyebrow">Status</th>
              <th className="px-3 py-2 eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Nothing pending your approval. Nice.</td></tr>
            )}
            {rows.map(x => (
              <ExpenseRow
                key={x.id}
                x={x}
                isOwner={false}
                canApprove={true}
                canPay={canPay}
                onApprove={approve}
                onReject={openReject}
              />
            ))}
          </tbody>
        </table>
      </div>

      {rejectDlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRejectDlg(null)}>
          <div className="bg-white border border-border w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="eyebrow flex items-center gap-2 mb-2"><XCircle size={12} weight="bold" /> Reject expense</div>
            <h3 className="font-display text-xl font-black mb-3">Provide a reason</h3>
            <p className="text-sm text-muted-foreground mb-3">
              {rejectDlg.user?.name} — {fmtMoney(rejectDlg.amount, rejectDlg.currency)} — {rejectDlg.category_meta?.label}
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Why is this being rejected?"
              className="w-full border border-border px-3 py-2 text-sm"
              data-testid="reject-reason"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setRejectDlg(null)} className="border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
              <button
                onClick={submitReject}
                disabled={!rejectReason.trim()}
                className="bg-[#FF3B30] text-white px-3 py-2 text-sm hover:opacity-90 disabled:opacity-40 flex items-center gap-1"
                data-testid="confirm-reject"
              >
                <XCircle size={14} weight="bold" /> Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========== ALL EXPENSES TAB (view_all_expenses) ===========
function AllExpensesTab({ cfg, projects }) {
  const { can } = useCapabilities();
  const canApprove = can("approve_expenses");
  const canPay = can("mark_expenses_paid");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rejectDlg, setRejectDlg] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (categoryFilter) params.set("category", categoryFilter);
      if (projectFilter) params.set("project_id", projectFilter);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const r = await api.get(`/expenses${params.toString() ? "?" + params.toString() : ""}`);
      setRows(r.data || []);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setLoading(false); }
  }, [statusFilter, categoryFilter, projectFilter, from, to]);
  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };
  const approve = async (x) => {
    try { await api.post(`/expenses/${x.id}/approve`); await load(); flash("ok", "Approved."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  const markPaid = async (x) => {
    try { await api.post(`/expenses/${x.id}/mark-paid`); await load(); flash("ok", "Marked as paid."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  const openReject = (x) => { setRejectDlg(x); setRejectReason(""); };
  const submitReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      await api.post(`/expenses/${rejectDlg.id}/reject`, { reason: rejectReason.trim() });
      setRejectDlg(null); setRejectReason("");
      await load();
      flash("ok", "Rejected.");
    } catch (e) { flash("error", formatAxiosError(e)); }
  };

  return (
    <div>
      <div className="mb-6">
        <p className="text-sm text-muted-foreground max-w-xl">
          All expenses across the organization. Use the filters to narrow down.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border border-border px-3 py-2 text-sm" placeholder="From" data-testid="all-from" />
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border border-border px-3 py-2 text-sm" placeholder="To" data-testid="all-to" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid="all-status">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="paid">Paid</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid="all-category">
          <option value="">All categories</option>
          {(cfg.categories || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid="all-project">
          <option value="">All projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="px-3 py-2 eyebrow">Date</th>
              <th className="px-3 py-2 eyebrow">Category</th>
              <th className="px-3 py-2 eyebrow">Submitter</th>
              <th className="px-3 py-2 eyebrow">Vendor</th>
              <th className="px-3 py-2 eyebrow">Project</th>
              <th className="px-3 py-2 eyebrow text-right">Amount</th>
              <th className="px-3 py-2 eyebrow">Status</th>
              <th className="px-3 py-2 eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No expenses match these filters.</td></tr>
            )}
            {rows.map(x => (
              <ExpenseRow
                key={x.id}
                x={x}
                isOwner={false}
                canApprove={canApprove && x.status === "submitted"}
                canPay={canPay && x.status === "approved"}
                onApprove={approve}
                onReject={openReject}
                onMarkPaid={markPaid}
              />
            ))}
          </tbody>
        </table>
      </div>

      {rejectDlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRejectDlg(null)}>
          <div className="bg-white border border-border w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="eyebrow flex items-center gap-2 mb-2"><XCircle size={12} weight="bold" /> Reject expense</div>
            <h3 className="font-display text-xl font-black mb-3">Provide a reason</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Why is this being rejected?"
              className="w-full border border-border px-3 py-2 text-sm"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setRejectDlg(null)} className="border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
              <button onClick={submitReject} disabled={!rejectReason.trim()} className="bg-[#FF3B30] text-white px-3 py-2 text-sm hover:opacity-90 disabled:opacity-40 flex items-center gap-1">
                <XCircle size={14} weight="bold" /> Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========== ANALYTICS TAB ===========
function AnalyticsTab({ cfg }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const r = await api.get(`/expenses/analytics/summary${params.toString() ? "?" + params.toString() : ""}`);
      setData(r.data);
      setErr("");
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading analytics…</div>;
  if (err) return <div className="text-sm text-[#FF3B30]">{err}</div>;
  if (!data) return null;

  const monthLabel = (m) => {
    if (!m || m === "0000-00") return "—";
    const [y, mo] = m.split("-");
    const d = new Date(Number(y), Number(mo) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };

  const monthData = (data.by_month || []).map(m => ({ month: monthLabel(m.month), total: m.total, count: m.count }));
  const catData = (data.by_category || []).map(c => ({ name: c.label, value: c.total, color: c.color, count: c.count }));
  const userData = (data.by_user || []).slice(0, 10).map(u => ({ name: u.user?.name || "Unknown", total: u.total, count: u.count }));
  const projData = (data.by_project || []).slice(0, 10).map(p => ({ name: p.project?.name || "—", total: p.total, count: p.count }));
  const statusData = (data.by_status || []).filter(s => s.count > 0);
  const methodData = (data.by_payment_method || []).filter(m => m.count > 0);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">
          Financial insights across all expense claims. Filter by a date range to focus on a specific period.
        </p>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid="analytics-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid="analytics-to" />
          <button onClick={() => { setFrom(""); setTo(""); }} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-1">
            <ArrowClockwise size={12} weight="bold" /> Reset
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-border p-4">
          <div className="eyebrow flex items-center gap-1"><Wallet size={10} weight="bold" /> Total spend</div>
          <div className="font-display text-3xl font-black">{fmtMoney(data.total_spend, data.currency)}</div>
          <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">{data.count} claim(s)</div>
        </div>
        <div className="border border-border p-4">
          <div className="eyebrow">Approved + paid</div>
          <div className="font-display text-3xl font-black text-[#22C55E]">
            {fmtMoney(
              statusData.filter(s => s.status === "approved" || s.status === "paid").reduce((a, b) => a + b.total, 0),
              data.currency
            )}
          </div>
        </div>
        <div className="border border-border p-4">
          <div className="eyebrow">Pending review</div>
          <div className="font-display text-3xl font-black text-[#F59E0B]">
            {fmtMoney(
              (statusData.find(s => s.status === "submitted") || { total: 0 }).total,
              data.currency
            )}
          </div>
        </div>
        <div className="border border-border p-4">
          <div className="eyebrow">Rejected</div>
          <div className="font-display text-3xl font-black text-[#FF3B30]">
            {fmtMoney(
              (statusData.find(s => s.status === "rejected") || { total: 0 }).total,
              data.currency
            )}
          </div>
        </div>
      </div>

      {/* Monthly trend */}
      <div className="border border-border p-4 mb-6">
        <div className="eyebrow mb-3">Monthly trend</div>
        {monthData.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center">No data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmtMoney(v, data.currency)} />
              <Line type="monotone" dataKey="total" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* By category */}
        <div className="border border-border p-4">
          <div className="eyebrow mb-3">Spend by category</div>
          {catData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={catData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={45}
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {catData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v, data.currency)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By status */}
        <div className="border border-border p-4">
          <div className="eyebrow mb-3">By status</div>
          {statusData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">No data yet.</div>
          ) : (
            <div className="space-y-2 mt-4">
              {statusData.map(s => {
                const pct = data.total_spend > 0 ? (s.total / data.total_spend) * 100 : 0;
                const st = STATUS_STYLES[s.status] || STATUS_STYLES.draft;
                return (
                  <div key={s.status}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className={`inline-block text-[9px] font-mono-mini px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                      <span className="font-semibold">{fmtMoney(s.total, data.currency)} • {s.count}</span>
                    </div>
                    <div className="h-2 bg-muted overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: st.cls.match(/#[0-9A-F]{6}/i)?.[0] || "#002FA7" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top spenders */}
        <div className="border border-border p-4">
          <div className="eyebrow mb-3">Top spenders</div>
          {userData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(240, userData.length * 32)}>
              <BarChart data={userData} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(v) => fmtMoney(v, data.currency)} />
                <Bar dataKey="total" fill="#002FA7" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top projects */}
        <div className="border border-border p-4">
          <div className="eyebrow mb-3">Spend by project</div>
          {projData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">No project-tagged expenses yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(240, projData.length * 32)}>
              <BarChart data={projData} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(v) => fmtMoney(v, data.currency)} />
                <Bar dataKey="total" fill="#7C3AED" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Payment method */}
      <div className="border border-border p-4">
        <div className="eyebrow mb-3">Payment method breakdown</div>
        {methodData.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center">No data yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {methodData.map(m => (
              <div key={m.method} className="border border-border p-3">
                <div className="eyebrow uppercase">{m.method}</div>
                <div className="font-display text-2xl font-black mt-1">{fmtMoney(m.total, data.currency)}</div>
                <div className="text-[10px] font-mono-mini text-muted-foreground">{m.count} claim(s)</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =========== ENTRY (TABS) ===========
export default function Expenses() {
  const { can } = useCapabilities();
  const location = useLocation();
  const navigate = useNavigate();

  const canUse = can("use_expenses");
  const canApprove = can("approve_expenses");
  const canViewAll = can("view_all_expenses");

  const urlParams = new URLSearchParams(location.search);
  const initialTab = urlParams.get("tab") || (canUse ? "mine" : canApprove ? "approvals" : canViewAll ? "all" : "mine");
  const [tab, setTab] = useState(initialTab);

  const [cfg, setCfg] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/expenses/config"),
          api.get("/projects").catch(() => ({ data: [] })),
        ]);
        setCfg(c.data);
        setProjects(p.data || []);
      } catch {
        // If /expenses/config fails, user is not authed — but ProtectedRoute should handle
      }
    })();
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("tab") !== tab) {
      p.set("tab", tab);
      navigate(`/expenses?${p.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!cfg) {
    return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;
  }

  const titles = { mine: "My expenses", approvals: "Approvals", all: "All expenses", analytics: "Analytics" };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <Wallet size={12} weight="bold" /> Finance / Expenses
        </div>
        <h1 className="font-display text-4xl font-black tracking-tight">{titles[tab] || "Expenses"}</h1>
        <div className="mt-1 text-sm text-muted-foreground">
          Base currency: <span className="font-mono-mini uppercase font-semibold">{cfg.default_currency}</span>
          &nbsp;•&nbsp; Approver role: <span className="font-mono-mini uppercase font-semibold">{cfg.approver_role}</span>
        </div>

        <div className="mt-6 flex items-center gap-1 border-b border-border -mb-8 flex-wrap">
          {canUse && (
            <button
              onClick={() => setTab("mine")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "mine" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-mine"
            >
              <Receipt size={12} weight="bold" /> My expenses
            </button>
          )}
          {canApprove && (
            <button
              onClick={() => setTab("approvals")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "approvals" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-approvals"
            >
              <CheckCircle size={12} weight="bold" /> Approvals
            </button>
          )}
          {canViewAll && (
            <button
              onClick={() => setTab("all")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "all" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-all"
            >
              <MagnifyingGlass size={12} weight="bold" /> All expenses
            </button>
          )}
          {(canViewAll || canUse) && (
            <button
              onClick={() => setTab("analytics")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "analytics" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-analytics"
            >
              <ChartPie size={12} weight="bold" /> Analytics
            </button>
          )}
        </div>
      </div>

      <div className="p-8 lg:p-12">
        {tab === "mine" && canUse && <MyExpensesTab cfg={cfg} projects={projects} />}
        {tab === "approvals" && canApprove && <ApprovalsTab cfg={cfg} />}
        {tab === "all" && canViewAll && <AllExpensesTab cfg={cfg} projects={projects} />}
        {tab === "analytics" && (canViewAll || canUse) && <AnalyticsTab cfg={cfg} />}
      </div>
    </div>
  );
}
